'use client';
import { useState, useRef, useEffect } from 'react';
import { api } from '@/lib/api';
import { useT } from '@/lib/useT';
import { Bot, X, Send, Loader2, Wrench, Sparkles } from 'lucide-react';

interface Message {
  role: 'user' | 'assistant';
  text: string;
  trace?: { name: string; ok: boolean }[];
}

const SUGGESTIONS = [
  'Покажи топ-5 самых убыточных orgs за месяц',
  'Сколько мы тратим на AI и какая фича дороже всего?',
  'Какие orgs используют ENTERPRISE план?',
  'Сколько compliance-нарушений критичных сейчас?',
  'Что произошло в системе за последний час?',
];

export function CopilotPanel() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, busy]);

  // ── Draggable launcher (vertical) — mirrors the support widget: press-hold →
  // move up/down → release to stick; a plain tap (no move) opens the panel.
  const POS_KEY = 'logixa_copilot_bottom';
  const [btnBottom, setBtnBottom] = useState(24);
  const bottomRef = useRef(24);
  const drag = useRef({ active: false, moved: false, startY: 0, startBottom: 24 });

  useEffect(() => {
    try {
      const v = parseInt(localStorage.getItem(POS_KEY) || '', 10);
      if (!Number.isNaN(v)) { setBtnBottom(v); bottomRef.current = v; }
    } catch { /* private mode */ }
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { active: true, moved: false, startY: e.clientY, startBottom: bottomRef.current };
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d.active) return;
    const dy = d.startY - e.clientY;
    if (Math.abs(dy) > 5) d.moved = true;
    const maxB = (typeof window !== 'undefined' ? window.innerHeight : 800) - 72;
    const nb = Math.max(16, Math.min(maxB, d.startBottom + dy));
    bottomRef.current = nb;
    setBtnBottom(nb);
  };
  const onPointerUp = () => {
    const d = drag.current;
    if (!d.active) return;
    d.active = false;
    if (d.moved) {
      try { localStorage.setItem(POS_KEY, String(Math.round(bottomRef.current))); } catch { /* noop */ }
    } else {
      setOpen(true);
    }
  };

  async function send(text: string) {
    const t = (text || input).trim();
    if (!t || busy) return;
    setInput('');
    const next: Message[] = [...messages, { role: 'user', text: t }];
    setMessages(next);
    setBusy(true);
    try {
      const history = next.slice(0, -1).map(m => ({
        role: m.role,
        content: m.text,
      }));
      const res = await api.post('/super-admin/copilot/ask', { history, text: t });
      setMessages([...next, { role: 'assistant', text: res.reply, trace: res.toolTrace }]);
    } catch (err: any) {
      setMessages([...next, { role: 'assistant', text: `⚠️ ${err.message || 'Error'}` }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          aria-label="Open AI Co-pilot"
          className="fixed right-6 w-14 h-14 rounded-full flex items-center justify-center z-40 touch-none cursor-grab active:cursor-grabbing select-none"
          style={{
            bottom: `${btnBottom}px`,
            background: 'linear-gradient(135deg, #FF652F 0%, #8B5CF6 100%)',
            boxShadow: '0 8px 32px rgba(139, 92, 246, 0.4)',
            color: 'white',
          }}>
          <Sparkles size={22} />
        </button>
      )}

      {/* Sidebar */}
      {open && (
        <div className="fixed top-0 right-0 h-screen w-[420px] z-50 flex flex-col"
          style={{ background: 'var(--c-panel)', borderLeft: '1px solid var(--c-border)' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4"
            style={{ borderBottom: '1px solid var(--c-border)' }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #FF652F 0%, #8B5CF6 100%)' }}>
                <Bot size={18} color="white" />
              </div>
              <div>
                <p className="font-bold" style={{ color: 'var(--c-heading)' }}>LOGIXA Co-pilot</p>
                <p className="text-[10px]" style={{ color: 'var(--c-muted)' }}>Sonnet 4.6 · 10 tools</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:opacity-70"
              style={{ color: 'var(--c-muted)' }}>
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center"
                  style={{ background: 'rgba(139,92,246,0.15)' }}>
                  <Sparkles size={26} color="#8B5CF6" />
                </div>
                <p className="text-sm font-semibold mb-1" style={{ color: 'var(--c-heading)' }}>
                  Frag mich alles über die Plattform
                </p>
                <p className="text-xs mb-4" style={{ color: 'var(--c-muted)' }}>
                  Ich kann Orgs durchsuchen, Finanzen analysieren, Compliance prüfen.
                </p>
                <div className="space-y-1.5">
                  {SUGGESTIONS.map(s => (
                    <button key={s} onClick={() => send(s)}
                      className="block w-full text-left text-xs px-3 py-2 rounded-lg hover:opacity-80"
                      style={{
                        background: 'var(--c-card)',
                        color: 'var(--c-text)',
                        border: '1px solid var(--c-border)',
                      }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className="max-w-[88%]" style={{
                  background: m.role === 'user' ? 'var(--c-accent)' : 'var(--c-card)',
                  color: m.role === 'user' ? '#fff' : 'var(--c-text)',
                  borderRadius: 14,
                  padding: '10px 12px',
                  border: m.role === 'user' ? 'none' : '1px solid var(--c-border)',
                }}>
                  {m.trace && m.trace.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {m.trace.map((t, j) => (
                        <span key={j} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]"
                          style={{
                            background: t.ok ? 'rgba(20,167,108,0.15)' : 'rgba(239,68,68,0.15)',
                            color: t.ok ? '#14A76C' : '#EF4444',
                          }}>
                          <Wrench size={9} />
                          {t.name}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="text-sm whitespace-pre-wrap" style={{ lineHeight: 1.5 }}>
                    {m.text}
                  </div>
                </div>
              </div>
            ))}

            {busy && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-2xl flex items-center gap-2"
                  style={{ background: 'var(--c-card)', border: '1px solid var(--c-border)' }}>
                  <Loader2 size={14} className="animate-spin" style={{ color: 'var(--c-accent)' }} />
                  <span className="text-xs" style={{ color: 'var(--c-muted)' }}>{t('copilot_thinking')}</span>
                </div>
              </div>
            )}

            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3" style={{ borderTop: '1px solid var(--c-border)' }}>
            <div className="flex gap-2">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send('')}
                disabled={busy}
                placeholder={t('copilot_ask_ph')}
                className="flex-1 px-3 py-2.5 rounded-xl text-sm"
                style={{
                  background: 'var(--c-bg)',
                  border: '1px solid var(--c-border)',
                  color: 'var(--c-heading)',
                }}/>
              <button onClick={() => send('')}
                disabled={busy || !input.trim()}
                className="w-10 h-10 rounded-xl flex items-center justify-center disabled:opacity-30"
                style={{ background: 'var(--c-accent)' }}>
                <Send size={14} color="white" />
              </button>
            </div>
            {messages.length > 0 && (
              <button onClick={() => setMessages([])}
                className="text-[10px] mt-2 opacity-50 hover:opacity-100"
                style={{ color: 'var(--c-muted)' }}>
                Clear conversation
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
